import { useStringExpression } from 'expression/useExpression';
import {
  DefaultExpressionValue,
  ViewAttributeChooserProp,
} from '../registry/Component.type';
import { isNotEmpty } from 'utils/isEmpty';
import {
  ExpressionValue,
  PropertyDefinitions,
} from '../registry/Component.type';
import { toolTipPosition } from '../registry/constants';
import { useTranslate } from '../../utils/lang';

export interface ToolTip {
  toolTip?: ExpressionValue;
  toolTipPosition?: keyof typeof toolTipPosition;
  toolTipMaxWidth?: number;
}

export const toolTipProps: PropertyDefinitions<ToolTip> = {
  toolTip: {
    label: 'Tooltip Text',
    type: 'expression',
    returnType: async () => 'string',
    helpText:
      'Specify a text/expression which appears when a cursor is positioned over.',
    category: 'Tooltip',
  },
  toolTipPosition: {
    label: 'Tootip Position',
    type: 'select',
    options: toolTipPosition,
    helpText: 'Select an option for the placement of the tooltip.',
    category: 'Tooltip',
  },
  toolTipMaxWidth: {
    label: 'Tootip Max Width',
    type: 'numberInput',
    helpText: 'Specifiy the max width in pixels.',
    category: 'Tooltip',
  },
};

export const ToolTipDefaultValue: Required<ToolTip> = {
  toolTip: DefaultExpressionValue,
  toolTipPosition: 'top',
  toolTipMaxWidth: 600,
};

export function useTooltipProps({
  appUid,
  pageId,
  itemId,
  toolTip,
  toolTipPosition: ttp,
  toolTipMaxWidth,
  recordId,
  rowIndex,
  hotKey,
  data = {},
}: Required<ToolTip> & {
  appUid: string;
  pageId: string;
  itemId: string;
  recordId?: string;
  rowIndex?: number;
  hotKey?: string;
  data?: Record<string, unknown>;
}) {
  let toolTipResult = useStringExpression({
    exp: toolTip,
    context: {
      appUid,
      pageId,
      itemId,
      recordId,
      rowIndex,
      property: 'toolTip',
      data,
    },
  });
  const { t } = useTranslate();
  if (toolTip.type === 'text') {
    toolTipResult = t(toolTipResult);
  }
  return isNotEmpty(toolTipResult)
    ? {
        'data-tip': toolTipResult + (hotKey ? ` (⌃⇧${hotKey})` : ''),
        'data-tip-at': ttp,
        'data-tip-delay': 0,
        'data-tip-width': toolTipMaxWidth,
      }
    : undefined;
}

export interface Attachment {
  storageConnectionCode?: ExpressionValue;
  bucketName?: ExpressionValue;
  dropzoneText?: string;
  images?: boolean;
  showImage?: boolean;
  imageHeight?: string;
  maxImageWidth?: string;
  accept?: string;
  versionAttribute?: string;
  showRevisions?: boolean;
  multiAttach?: boolean;
}

export const attachmentProps: PropertyDefinitions<Attachment> = {
  storageConnectionCode: {
    label: 'Storage Connection Code',
    type: 'expression',
    returnType: async () => 'string',
  },
  bucketName: {
    label: 'Bucket Name',
    type: 'expression',
    returnType: async () => 'string',
    helpText:
      'Specify a bucket name to override the default bucket configured on the storage connection.',
  },
  dropzoneText: {
    label: 'Dropzone Text',
    type: 'string',
  },
  images: {
    label: 'Images',
    type: 'boolean',
    helpText: 'Check this to accept only image types.',
  },
  showImage: {
    label: 'Render Image',
    type: 'boolean',
    helpText: 'Check this to render the image instead of the file name link.',
  },
  multiAttach: {
    label: 'Multi Attachments',
    type: 'boolean',
    helpText: 'Check this to attach multiple files',
  },
  imageHeight: {
    label: 'Image Height',
    type: 'string',
    helpText: 'Specify the height of the image in pixels.',
  },
  maxImageWidth: {
    label: 'Max Image Width',
    type: 'string',
    helpText: 'Specify the max width of the image in pixels or percentage.',
  },
  accept: {
    label: 'Accept',
    type: 'string',
    helpText: `Specify comma separated list of types to accept. eg. .pdf, .png, .jpeg.

Refer this for more info [react-dropzone](https://react-dropzone.js.org/#section-accepting-specific-file-types)`,
  },
  versionAttribute: {
    label: 'Version Attribute',
    type: 'ViewAttributeChooser',
    aliasType: 'STORE',
    helpText:
      'Choose an attribute that contains file version of the given attachment. Only attributes with type Integer are allowed.',
    getAttributeDataTypes: () => ['Integer'],
    category: 'Data',
  } as ViewAttributeChooserProp,
  showRevisions: {
    label: 'Show Revisions',
    type: 'boolean',
  },
};

export const AttachmentDefaultValue: Required<Attachment> = {
  storageConnectionCode: { type: 'text', value: '' },
  bucketName: { type: 'text', value: '' },
  dropzoneText: '',
  images: false,
  showImage: false,
  imageHeight: '200px',
  maxImageWidth: '100%',
  accept: '',
  versionAttribute: '',
  showRevisions: false,
  multiAttach: false,
};
